import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { RoomConflictResultDTO } from "@/modules/classes/domain/contracts/sessions";
import { classesApi } from "@/modules/classes/presentation/api/client";
import { addMinutesToTimeHhMm, toTimeHHmmss } from "@/shared/lib/date-time";
import type { PrivateLessonSlotDurationMinutes } from "@/shared/lib/private-lesson-slot-duration";
import type { RehearsalStudioFormSlice } from "./rehearsal-session-form-types";

export type RehearsalDateConflictDetail = {
  date: string;
  slotStartTime: string;
  slotEndTime: string;
  result: RoomConflictResultDTO;
};

export type RehearsalStudioConflictState = {
  hasConflict: boolean;
  message: string | null;
  details: RehearsalDateConflictDetail[];
};

export type RehearsalStudioConflictOptions = {
  excludeRoomBookingId?: string;
};

const EMPTY_CONFLICT_STATE: RehearsalStudioConflictState = {
  hasConflict: false,
  message: null,
  details: [],
};

export function buildRehearsalStudioConflictMessage(
  conflictCount: number,
  totalDates: number,
): string {
  if (conflictCount === totalDates) {
    return totalDates === 1
      ? "ຫ້ອງຖືກຈອງໃນເວລານີ້ແລ້ວ"
      : "ຫ້ອງຖືກຈອງໃນເວລານີ້ແລ້ວທຸກວັນທີ່ເລືອກ";
  }
  return `ຫ້ອງຖືກຈອງແລ້ວ ${conflictCount} ວັນ — ເບິ່ງລາຍລະອຽດຂ້າງລຸ່ມ`;
}

export async function checkRehearsalStudioConflicts(
  values: RehearsalStudioFormSlice,
  canBookStudio: boolean,
  options: RehearsalStudioConflictOptions = {},
): Promise<RehearsalStudioConflictState> {
  if (!canBookStudio || !values.bookStudio) {
    return EMPTY_CONFLICT_STATE;
  }

  const roomId = values.studioRoomId?.trim();
  const startTime = values.studioStartTime?.trim();
  const dates = values.scheduledDates ?? [];

  if (!roomId || !startTime || dates.length === 0) {
    return EMPTY_CONFLICT_STATE;
  }

  const endTime = addMinutesToTimeHhMm(
    startTime,
    values.studioSlotMinutes as PrivateLessonSlotDurationMinutes,
  );
  const startTimeHms = toTimeHHmmss(startTime);
  const endTimeHms = toTimeHHmmss(endTime);

  try {
    const results = await Promise.all(
      dates.map(async (date) => ({
        date,
        result: await classesApi.sessions.checkConflict({
          roomId,
          date,
          startTime: startTimeHms,
          endTime: endTimeHms,
          excludeRoomBookingId: options.excludeRoomBookingId,
        }),
      })),
    );

    const details: RehearsalDateConflictDetail[] = results
      .filter(({ result }) => result.hasConflict)
      .map(({ date, result }) => ({
        date,
        slotStartTime: startTimeHms,
        slotEndTime: endTimeHms,
        result,
      }));

    if (details.length === 0) {
      return EMPTY_CONFLICT_STATE;
    }

    return {
      hasConflict: true,
      message: buildRehearsalStudioConflictMessage(
        details.length,
        dates.length,
      ),
      details,
    };
  } catch {
    return {
      hasConflict: true,
      message: "ບໍ່ສາມາດກວດສອບຫ້ອງວ່າງໄດ້ — ລອງໃໝ່",
      details: [],
    };
  }
}

export async function runRehearsalStudioConflictCheck(
  values: RehearsalStudioFormSlice,
  canBookStudio: boolean,
  options: RehearsalStudioConflictOptions = {},
): Promise<
  | { ok: true }
  | { ok: false; message: string; details: RehearsalDateConflictDetail[] }
> {
  const state = await checkRehearsalStudioConflicts(
    values,
    canBookStudio,
    options,
  );
  if (!state.hasConflict) {
    return { ok: true };
  }
  return {
    ok: false,
    message: state.message ?? "ຫ້ອງຖືກຈອງໃນເວລານີ້ແລ້ວ",
    details: state.details,
  };
}

export type RehearsalStudioConflictMonitorInput = {
  bookStudio?: boolean;
  studioRoomId?: string;
  scheduledDates: string[];
  studioStartTime?: string;
  studioSlotMinutes: RehearsalStudioFormSlice["studioSlotMinutes"];
  excludeRoomBookingId?: string;
};

export function useRehearsalStudioConflictMonitor(
  canBookStudio: boolean,
  input: RehearsalStudioConflictMonitorInput,
  enabled = true,
) {
  const [conflictState, setConflictState] =
    useState<RehearsalStudioConflictState>(EMPTY_CONFLICT_STATE);
  const [isChecking, setIsChecking] = useState(false);

  const monitorValues = useMemo<RehearsalStudioFormSlice>(
    () => ({
      scheduledDates: input.scheduledDates,
      bookStudio: !!input.bookStudio,
      studioRoomId: input.studioRoomId,
      studioStartTime: input.studioStartTime,
      studioSlotMinutes: input.studioSlotMinutes,
    }),
    [
      input.bookStudio,
      input.scheduledDates,
      input.studioRoomId,
      input.studioSlotMinutes,
      input.studioStartTime,
    ],
  );

  const conflictOptions = useMemo(
    () => ({
      excludeRoomBookingId: input.excludeRoomBookingId,
    }),
    [input.excludeRoomBookingId],
  );

  useEffect(() => {
    if (!enabled) {
      setConflictState(EMPTY_CONFLICT_STATE);
      setIsChecking(false);
      return;
    }
    if (!canBookStudio || !input.bookStudio) {
      setConflictState(EMPTY_CONFLICT_STATE);
      setIsChecking(false);
      return;
    }
    if (
      !input.studioRoomId?.trim() ||
      input.scheduledDates.length === 0 ||
      !input.studioStartTime
    ) {
      setConflictState(EMPTY_CONFLICT_STATE);
      setIsChecking(false);
      return;
    }

    let cancelled = false;
    setIsChecking(true);

    void checkRehearsalStudioConflicts(
      monitorValues,
      canBookStudio,
      conflictOptions,
    )
      .then((state) => {
        if (!cancelled) setConflictState(state);
      })
      .catch(() => {
        if (!cancelled) {
          setConflictState({
            hasConflict: true,
            message: "ບໍ່ສາມາດກວດສອບຫ້ອງວ່າງໄດ້ — ລອງໃໝ່",
            details: [],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    canBookStudio,
    input.bookStudio,
    input.scheduledDates,
    input.studioRoomId,
    input.studioStartTime,
    input.studioSlotMinutes,
    monitorValues,
    conflictOptions,
  ]);

  const runConflictCheck = useCallback(
    async (
      values: RehearsalStudioFormSlice,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      setIsChecking(true);
      try {
        const state = await checkRehearsalStudioConflicts(
          values,
          canBookStudio,
          conflictOptions,
        );
        setConflictState(state);
        if (!state.hasConflict) {
          return { ok: true };
        }
        return {
          ok: false,
          message: state.message ?? "ຫ້ອງຖືກຈອງໃນເວລານີ້ແລ້ວ",
        };
      } catch {
        const message = "ບໍ່ສາມາດກວດສອບຫ້ອງວ່າງໄດ້ — ລອງໃໝ່";
        setConflictState({
          hasConflict: true,
          message,
          details: [],
        });
        return { ok: false, message };
      } finally {
        setIsChecking(false);
      }
    },
    [canBookStudio, conflictOptions],
  );

  return useMemo(
    () => ({
      conflictMessage: conflictState.message,
      conflictDetails: conflictState.details,
      hasConflict: conflictState.hasConflict,
      isChecking,
      submitBlocked:
        enabled &&
        canBookStudio &&
        !!input.bookStudio &&
        (isChecking || conflictState.hasConflict),
      runConflictCheck,
    }),
    [
      canBookStudio,
      conflictState,
      enabled,
      input.bookStudio,
      isChecking,
      runConflictCheck,
    ],
  );
}

type RehearsalStudioConflictContextValue = ReturnType<
  typeof useRehearsalStudioConflictMonitor
>;

const RehearsalStudioConflictContext =
  createContext<RehearsalStudioConflictContextValue | null>(null);

export function RehearsalStudioConflictProvider({
  value,
  children,
}: {
  value: RehearsalStudioConflictContextValue;
  children: ReactNode;
}) {
  return (
    <RehearsalStudioConflictContext.Provider value={value}>
      {children}
    </RehearsalStudioConflictContext.Provider>
  );
}

export function useRehearsalStudioConflict() {
  const ctx = useContext(RehearsalStudioConflictContext);
  if (!ctx) {
    throw new Error(
      "useRehearsalStudioConflict must be used within RehearsalStudioConflictProvider",
    );
  }
  return ctx;
}
